import { type ExtensionApi } from '../../../core/extension';
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import { fontWeight } from '../../../ui/table/util/const';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'stackBoardBar',
  name_en: 'stackBoardBar',
  // name_zh: '叠加看板柱状图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 1,
    z: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    z: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    }
  }
};

/**
 * ECharts 的配置项
 * @see https://echarts.apache.org/zh/option.html#series-line
 */
// type EChartsPieSeries = {
//   roseType?: unknown;
//   radius?: unknown;
// };

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    z: string;
  };
}

export const stackBoardBar = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec) {
    const { data: dataArray, util: utils } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, z }
    } = spec;
    const data = dataArray[dataIndex];
    const transformData = utils.dataTransform.transform(data.values.slice(), [
      { type: 'sort', field: x, order: 'ascending' }
    ]);
    const { x: xAxisData } = utils.dataEncoding.encoding(transformData, {
      x: {
        field: x,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      },
      y: { field: y[0], transforms: [{ type: 'toNumeric' }] },

      z: { field: z, transforms: [{ type: 'deDuplication' }] }
    });
    const zDatas = data.values.map(item => item[z]);
    const series = [
      {
        type: 'custom',
        name: y,
        renderItem(params, api) {
          const zData = zDatas[params.dataIndex];
          const zDataMax = Math.max(...zData);
          const zDataMin = Math.min(...zData);
          const boardHeight = 26;
          const boardBottom = 6;
          const lineContainerHeight = 9;
          // 极值差值
          let arrayData = 0;
          if (zDataMax !== zDataMin) {
            arrayData = Math.abs(zDataMax - zDataMin);
          }
          const xValue = api.value(0);
          const highPoint = api.coord([xValue, api.value(1)]);
          const barLayout = api.barLayout({
            count: 1
          });
          const halfWidth = barLayout[0].width * 0.5;
          let symbols = [];
          const cornerRadius = 3; // 方便修改圆角大小
          const pathCommands = `
            M ${highPoint[0] - halfWidth + cornerRadius} ${
              highPoint[1] - boardHeight - boardBottom
            }  
            A ${cornerRadius} ${cornerRadius} 0 0 0 ${highPoint[0] - halfWidth} ${
              highPoint[1] - boardHeight - boardBottom + cornerRadius
            }  
            L ${highPoint[0] - halfWidth} ${highPoint[1] - boardBottom - cornerRadius}  
            A ${cornerRadius} ${cornerRadius} 0 0 0 ${highPoint[0] - halfWidth + cornerRadius} ${
              highPoint[1] - boardBottom
            }  
            
            L ${highPoint[0] - 3} ${highPoint[1] - boardBottom}  
            L ${highPoint[0]} ${highPoint[1] - boardBottom + 4}  
            L ${highPoint[0] + 3} ${highPoint[1] - boardBottom}  
            
            L ${highPoint[0] + halfWidth - cornerRadius} ${highPoint[1] - boardBottom}  
            A ${cornerRadius} ${cornerRadius} 0 0 0 ${highPoint[0] + halfWidth} ${
              highPoint[1] - boardBottom - cornerRadius
            }  
            L ${highPoint[0] + halfWidth} ${
              highPoint[1] - boardHeight - boardBottom + cornerRadius
            }  
            A ${cornerRadius} ${cornerRadius} 0 0 0 ${highPoint[0] + halfWidth - cornerRadius} ${
              highPoint[1] - boardHeight - boardBottom
            }  
            Z
          `;
          if (arrayData && zData.length > 1) {
            const lineDataArray = zData.map((ele, index) => {
              return [
                (((halfWidth - 2) * 2) / (zData.length - 1)) * index - halfWidth + 2,
                lineContainerHeight -
                  (lineContainerHeight / arrayData) * (ele - zDataMin) -
                  lineContainerHeight -
                  boardBottom -
                  3
              ];
            });
            const lineData = lineDataArray.map(item => {
              return [highPoint[0] + item[0], highPoint[1] + item[1]];
            });

            symbols = [
              {
                type: 'path',
                shape: {
                  d: pathCommands
                },
                style: {
                  fill: '#FFFFFF',
                  stroke: '#d8d8e0',
                  lineWidth: 1,
                  shadowColor: 'rgba(41,41,51,0.05)',
                  shadowOffsetY: 2
                }
              },
              {
                type: 'polygon',
                shape: {
                  points: [
                    ...lineData,
                    [highPoint[0] + halfWidth - 1, highPoint[1] - boardBottom],
                    [highPoint[0] - halfWidth + 1, highPoint[1] - boardBottom]
                  ],
                  smooth: 0.2
                },
                style: {
                  fill: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {
                      offset: 0,
                      color:
                        zData[0] > zData[zData.length - 1]
                          ? 'rgba(11,151,92,0.25)'
                          : 'rgba(237,45,0,0.25)'
                    }, // 顶部颜色（蓝色，不透明）
                    { offset: 1, color: 'rgba(255, 255, 255, 0.1)' } // 底部颜色（透明）
                  ]),
                  stroke: 'none'
                }
              },
              {
                type: 'polyline',
                shape: {
                  points: lineData,
                  smooth: 0.2
                },
                style: {
                  fill: 'none',
                  stroke:
                    zData[0] > zData[zData.length - 1] ? 'rgba(11,151,92,1)' : 'rgba(237,45,0,1)'
                }
              },
              {
                type: 'text',
                style: {
                  x: highPoint[0],
                  y: highPoint[1] - boardHeight - boardBottom + 4,
                  text: zData[zData.length - 1],
                  fontSize: 10,
                  fontWeight: 'bold',
                  align: 'center',
                  lineWidth: 2,
                  fill:
                    zData[0] > zData[zData.length - 1] ? 'rgba(11,151,92,1)' : 'rgba(237,45,0,1)'
                }
              }
            ];
          } else {
            symbols = [
              {
                type: 'path',
                shape: {
                  d: pathCommands
                },
                style: {
                  fill: '#FFFFFF',
                  stroke: '#d8d8e0',
                  lineWidth: 1,
                  shadowColor: 'rgba(41,41,51,0.05)',
                  shadowOffsetY: 2
                }
              },
              {
                type: 'polygon',
                shape: {
                  points: [
                    [
                      highPoint[0] - halfWidth - 1,
                      highPoint[1] - boardBottom - lineContainerHeight
                    ],
                    [
                      highPoint[0] + halfWidth - 1,
                      highPoint[1] - boardBottom - lineContainerHeight
                    ],

                    [highPoint[0] + halfWidth - 1, highPoint[1] - boardBottom],
                    [highPoint[0] - halfWidth + 1, highPoint[1] - boardBottom]
                  ],
                  smooth: 0.2
                },
                style: {
                  fill: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {
                      offset: 0,
                      color: 'rgba(237,45,0,0.25)'
                    }, // 顶部颜色（蓝色，不透明）
                    { offset: 1, color: 'rgba(255, 255, 255, 0.1)' } // 底部颜色（透明）
                  ]),
                  stroke: 'none'
                }
              },
              {
                type: 'polyline',
                shape: {
                  points: [
                    [
                      highPoint[0] - halfWidth - 1,
                      highPoint[1] - boardBottom - lineContainerHeight
                    ],
                    [highPoint[0] + halfWidth - 1, highPoint[1] - boardBottom - lineContainerHeight]
                  ],
                  smooth: 0.2
                },
                style: {
                  fill: 'none',
                  stroke: 'rgba(237,45,0,1)'
                }
              },
              {
                type: 'text',
                style: {
                  x: highPoint[0],
                  y: highPoint[1] - boardHeight - boardBottom + 4,
                  text: zData[zData.length - 1] || '--',
                  fontSize: 10,
                  fontWeight: 'bold',
                  align: 'center',
                  lineWidth: 2,
                  fill: 'rgba(237,45,0,1)'
                }
              }
            ];
          }
          return {
            type: 'group',
            children: symbols
          };
        },
        itemStyle: {
          borderWidth: 2
        },
        encode: {
          x: 0,
          y: 1
        },
        data: dataArray[0].values.map(item => item[y]),
        z: 100
      },
      {
        type: 'bar',
        name: y,
        data: dataArray[0].values.map(item => item[y])
      }
    ];
    /**
     *  配置Series
     * @returns
     */
    function configureSeriesOption() {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        xAxis: {
          show: true,
          type: 'category',
          data: xAxisData,
          axisPointer: {
            show: true,
            type: 'shadow',
            shadowStyle: {
              color: 'rgba(172,198,251)',
              opacity: 0.3
            }
          }
        },
        yAxis: {
          show: true,
          type: 'value',
          __dataIndex: 0,
          __encodeField: [y]
        },
        series
      };
    }
    // * -------------------------- chart
    return renderApi => {
      const renderParams = {
        option: renderApi.mergeChartOptionProxy(configureSeriesOption())
      };
      renderParams.option.legend.data = [renderParams.option.legend.data[0]];
      renderApi.renderLegendProxy(renderParams.option.legend.data);
      renderApi.renderChartProxy(renderParams);
    };
  }
});
